import { useState } from "react";
import { Menu, X, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link, useLocation } from "react-router-dom";
import { useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import { NeumorphicLogoBox, NeumorphicNavButton } from "@/components/neumorphic";

export const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  
  // Try to get theme, but provide fallback for pages without provider
  let theme = "light";
  try {
    const themeContext = useNeumorphicTheme();
    theme = themeContext.theme;
  } catch {
    // Not wrapped in provider, use default
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const menuItems = [
    { to: "/", label: "Home", shortLabel: "Home" },
    { to: "/who-we-are", label: "Who We Are", shortLabel: "Us" },
    { to: "/mission", label: "Our Mission", shortLabel: "Mission" },
    { to: "/vision", label: "Our Vision", shortLabel: "Vision" },
    { to: "/why-uda", label: "Why UDA?", shortLabel: "Why" },
    { to: "/our-work", label: "Our Work", shortLabel: "Work" },
    { to: "/landing", label: "UDA Wizard", shortLabel: "Wizard" },
    { to: "/uda-models", label: "UDA Models", shortLabel: "Models" },
    { to: "/adu-calculator", label: "Calculator", shortLabel: "Calc" },
    { to: "/follow-us", label: "Follow Us", shortLabel: "Follow" },
    { to: "/faq", label: "FAQ", shortLabel: "FAQ" },
    { to: "/contact-us", label: "Contact Us", shortLabel: "Contact" },
    { to: "/admin-login", label: "Login", shortLabel: "Login", icon: LogIn }
  ];

  const headerBg = theme === "dark" ? "bg-[#0a0a0a]" : "bg-[#f5f5f5]";

  if (isMobile) {
    return (
      <>
        {/* Header with gray neumorphic background */}
        <header className={`w-full px-4 relative z-50 md:py-[29px] mb-0 py-0 ${headerBg}`}>
          <div className="max-w-7xl mx-auto flex items-start justify-between">
            {/* Empty left space for balance */}
            <div className="w-20"></div>
            
            {/* Centered Logo - raised without frame */}
            <div className="flex-1 flex justify-center pt-4 md:pt-0">
              <NeumorphicLogoBox>
                <Link to="/">
                  <video 
                    src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/UPDATED%20OFFICIAL%20LOGO%20VIDEO%20.m4v?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvVVBEQVRFRCBPRkZJQ0lBTCBMT0dPIFZJREVPIC5tNHYiLCJpYXQiOjE3NTMyMTA3ODksImV4cCI6MjA2ODU3MDc4OX0.OsUvgtIF-9jWpNHQ01Md219Rwylph-NiLR0_biMPjck"
                    className="w-auto h-20" 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  />
                </Link>
              </NeumorphicLogoBox>
            </div>
            
            {/* Menu Button */}
            <div className="relative mx-[17px] mt-8">
              <Button variant="ghost" size="icon" onClick={toggleMenu} className="w-12 h-12 rounded-full bg-[#e4683b] hover:bg-[#d55a32] transition-all duration-300 shadow-lg hover:shadow-xl my-0 py-[18px]">
                <div className="relative py-[21px]">
                  {isMenuOpen ? <X className="h-5 w-5 text-white transition-transform duration-300" /> : <Menu className="h-5 w-5 text-white transition-transform duration-300" />}
                </div>
              </Button>
            </div>
          </div>
        </header>

        {/* Menu Overlay */}
        {isMenuOpen && (
          <div className="fixed inset-0 z-40">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />
            <div className="absolute top-20 left-0 right-0 z-50 px-4">
              <nav className="bg-black/80 backdrop-blur-xl w-full shadow-2xl overflow-hidden">
                <div className="py-8">
                  {menuItems.map(item => (
                    <Link 
                      key={item.to} 
                      to={item.to} 
                      className="block text-xl font-thin text-white hover:text-white/80 transition-all duration-200 hover:bg-white/10 py-4 text-center cursor-pointer select-none" 
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              </nav>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop version with neumorphic styling
  return (
    <>
      {/* Header with gray background and raised logo */}
      <header className={`w-full px-4 relative z-50 py-[29px] md:mt-0 ${headerBg}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-center relative">
          <NeumorphicLogoBox>
            <Link to="/">
              <video 
                src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/UPDATED%20OFFICIAL%20LOGO%20VIDEO%20.m4v?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvVVBEQVRFRCBPRkZJQ0lBTCBMT0dPIFZJREVPIC5tNHYiLCJpYXQiOjE3NTMyMTA3ODksImV4cCI6MjA2ODU3MDc4OX0.OsUvgtIF-9jWpNHQ01Md219Rwylph-NiLR0_biMPjck"
                className="w-auto h-28" 
                autoPlay 
                loop 
                muted 
                playsInline
              />
            </Link>
          </NeumorphicLogoBox>
        </div>
      </header>

      {/* Navigation Menu with recessed/raised buttons */}
      <nav className={`w-full py-[12px] mt-0 ${headerBg}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center flex-nowrap gap-[14px]">
            {menuItems.map(item => (
              <NeumorphicNavButton 
                key={item.to} 
                to={item.to}
                isActive={location.pathname === item.to}
                label={item.shortLabel}
              >
                {item.label}
              </NeumorphicNavButton>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
};
